import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, Trophy } from "lucide-react";
import { api, errCode, formatXAF } from "@/lib/api";
import { LudoBoard, LudoTable } from "@/components/LudoBoard";
import { Die } from "@/components/Dice";
import { COLORS } from "@/lib/ludoBoard";
import { useI18n } from "@/i18n/I18nContext";
import { playCapture, playDice, playHome, playSteps, playWin } from "@/lib/sound";
import { SoundToggle } from "@/components/SoundToggle";
import { GameChat } from "@/components/GameChat";
import { BackToLobby } from "@/components/BackToLobby";

const actKey = (a) => `${a.at}|${a.type}|${a.by || a.player}|${a.piece ?? ""}`;

const useLudoSounds = (g) => {
  const seen = useRef(null);
  useEffect(() => {
    if (!g) { return; }
    const keys = g.actions.map(actKey);
    if (seen.current === null) { seen.current = new Set(keys); return; }
    const fresh = g.actions.filter((a, i) => !seen.current.has(keys[i]));
    keys.forEach((k) => seen.current.add(k));
    fresh.slice(-3).forEach((a, i) => {
      setTimeout(() => {
        if (a.type === "roll") playDice();
        else if (a.type === "move") {
          playSteps(a.from === -1 ? 1 : a.to - a.from);
          if (a.captured?.length) setTimeout(playCapture, 450);
          else if (a.to === 56) setTimeout(playHome, 450);
        }
      }, i * 650);
    });
  }, [g]);
  const status = useRef(null);
  useEffect(() => {
    if (g && status.current === "in_progress" && g.status === "finished" && g.winners?.includes(g.me)) playWin();
    status.current = g?.status;
  }, [g]);
};

const useCountdown = (iso) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const f = () => setLeft(Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 1000)));
    f();
    const id = setInterval(f, 250);
    return () => clearInterval(id);
  }, [iso]);
  return left;
};

const Players = ({ g }) => {
  const { t } = useI18n();
  const left = useCountdown(g.turn_deadline);
  const cur = g.state.players[g.state.turn]?.id;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="ludo-players">
      {g.state.players.map((pl) => (
        <div key={pl.id} className={`rounded-xl border p-2 text-xs ${cur === pl.id && g.status === "in_progress" ? "border-amber-500/60 bg-amber-500/10" : "border-white/10"}`} data-testid={`ludo-player-${pl.color}`}>
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ background: COLORS[pl.color] }} />
            <span className="truncate font-semibold">{g.pseudos[pl.id]}{pl.id === g.me ? ` (${t("ludo.you")})` : ""}</span>
          </div>
          <p className="mt-1 text-slate-400">
            {pl.status === "forfeit" ? t("ludo.forfeit") : `${pl.pieces.filter((p) => p >= 56).length}/${pl.pieces.length} ${t("ludo.home")}`}
            {cur === pl.id && g.status === "in_progress" && !g.waiting_for?.length && <span className="ml-2 font-mono text-amber-300" data-testid={`ludo-timer-${pl.color}`}>{left}s</span>}
          </p>
        </div>
      ))}
    </div>
  );
};

const Rematch = ({ g }) => {
  const { t, tErr } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const r = g.rematch;
  const open = r && r.status === "open";
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ludo/games/${g.id}/rematch`);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      navigate(data.status === "matched" ? `/ludo/game/${data.game_id}` : "/ludo/queue");
    } catch (e) { toast.error(tErr(errCode(e))); } finally { setBusy(false); }
  };
  if (r && r.status === "played" && r.game_id) return <Link to={`/ludo/game/${r.game_id}`} className="text-sm text-amber-300 underline" data-testid="ludo-rematch-started">{t("ludo.rematchStarted")}</Link>;
  if (r && !open && r.status !== "cancelled") return <p className="text-sm text-slate-400" data-testid="ludo-rematch-closed">{t("ludo.rematchClosed")}</p>;
  return (
    <div className="space-y-2 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="ludo-rematch">
      {r?.status === "cancelled" && <p className="text-sm text-slate-400" data-testid="ludo-rematch-closed">{t("ludo.rematchClosed")}</p>}
      {open && <p className="text-sm text-amber-200" data-testid="ludo-rematch-offer">{t("ludo.rematchOffer", { name: r.creator_pseudo, n: r.joined.length, total: g.player_count })}</p>}
      <button type="button" onClick={go} disabled={busy} data-testid="ludo-rematch-btn"
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-bold text-[#0B0D12] transition-transform active:scale-95 disabled:opacity-60">
        <RotateCcw className="h-4 w-4" /> {open ? t("ludo.rematchAccept", { stake: formatXAF(g.stake) }) : t("ludo.rematch", { stake: formatXAF(g.stake) })}
      </button>
    </div>
  );
};

const End = ({ g }) => {
  const { t } = useI18n();
  const won = g.winners?.includes(g.me);
  const inRematch = g.rematch?.joined?.includes(g.me);
  return (
    <div className="surface space-y-3 p-5" data-testid="ludo-end">
      <p className="flex items-center gap-2 font-display text-xl font-bold" data-testid="ludo-end-text">
        <Trophy className="h-5 w-5 text-amber-400" />
        {g.status === "cancelled" ? t("ludo.cancelled") : won ? `${t("ludo.youWin")} ${formatXAF(g.settlement?.payouts?.[g.me] || 0)}` : `${t("ludo.winner")} ${g.winners?.map((w) => g.pseudos[w]).join(", ")}`}
      </p>
      {g.finish_reason === "time_limit" && <p className="text-sm text-slate-400">{t("ludo.timeLimit")}</p>}
      {g.status === "cancelled" && <p className="text-sm text-slate-300" data-testid="ludo-end-refund">{t("ludo.refundInfo", { s: formatXAF(g.stake) })}</p>}
      {g.room_code && !inRematch && <Rematch g={g} />}
      {inRematch && <Link to="/ludo/queue" className="text-sm text-amber-300 underline" data-testid="ludo-rematch-waiting">{t("ludo.rematchWaiting")}</Link>}
      <div className="flex flex-col gap-2 sm:flex-row">
        {g.status === "finished" && <Link to={`/ludo/verify/${g.id}`} className="inline-flex h-11 items-center justify-center rounded-full border border-white/15 px-5 text-sm font-semibold" data-testid="ludo-verify-link">{t("ludo.verify")}</Link>}
        <Link to="/lobby?game=ludo" className="inline-flex h-11 items-center justify-center rounded-full bg-amber-500 px-5 text-sm font-bold text-[#0B0D12]" data-testid="ludo-again-link">{t("ludo.again")}</Link>
        <Link to="/lobby?game=ludo" className="inline-flex h-11 items-center justify-center rounded-full border border-white/15 px-5 text-sm font-semibold" data-testid="ludo-end-back-lobby">{t("dames.backLobby")}</Link>
      </div>
    </div>
  );
};

export default function LudoGamePage() {
  const { id } = useParams();
  const { t, tErr } = useI18n();
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const prevStatus = useRef(null);
  useEffect(() => {
    if (g && prevStatus.current !== g.status && ["finished", "cancelled"].includes(g.status)) {
      qc.invalidateQueries({ queryKey: ["wallet"] });
    }
    prevStatus.current = g?.status;
  }, [g, qc]);
  const load = useCallback(() => api.get(`/ludo/games/${id}`).then(({ data }) => setG(data)).catch(() => {}), [id]);
  useLudoSounds(g);
  useEffect(() => {
    load();
    const iv = setInterval(load, 1000);
    return () => clearInterval(iv);
  }, [load]);
  const send = async (path, body) => {
    setBusy(true);
    try { setG((await api.post(`/ludo/games/${id}/${path}`, body)).data); } catch (e) { toast.error(tErr(errCode(e))); } finally { setBusy(false); }
  };
  if (!g) return null;
  const st = g.state;
  const mineTurn = g.status === "in_progress" && st.players[st.turn]?.id === g.me;
  const last = [...g.actions].reverse().find((a) => a.type === "roll");
  const over = g.status !== "in_progress" && g.status !== "settling";
  return (
    <div className="mx-auto max-w-2xl space-y-4 rise" data-testid="ludo-game-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          {over && <BackToLobby to="/lobby?game=ludo" testId="ludo-header-back" />}
          <h1 className="font-display text-2xl font-extrabold">Ludo</h1>
        </div>
        <div className="flex items-center gap-2">
          <GameChat kind="ludo" gid={id} />
          <SoundToggle />
          <span className="rounded-full bg-white/5 px-3 py-1 text-xs" data-testid="ludo-pot">{t("ludo.pot")} {formatXAF(g.pot)}</span>
        </div>
      </div>
      <Players g={g} />
      {over && <End g={g} />}
      <div className="flex justify-center"><LudoTable><LudoBoard state={st} me={g.me} legal={g.legal} die={st.die} onPick={(i) => !busy && send("move", { piece: i })} /></LudoTable></div>
      {g.status === "in_progress" && g.waiting_for?.length > 0 && (
        <p className="surface p-4 text-center text-sm font-semibold text-amber-300" data-testid="ludo-waiting-for">{t("game.waitingFor", { names: g.waiting_for.join(", ") })}</p>
      )}
      {g.status === "in_progress" && !g.waiting_for?.length && (
        <div className="flex items-center gap-4" data-testid="ludo-controls">
          {last && <div className="flex items-center gap-2 text-sm text-slate-300" data-testid="ludo-last-roll"><Die value={last.die} size={40} /> {g.pseudos[last.by]}</div>}
          {mineTurn && st.phase === "roll" && <button type="button" disabled={busy} onClick={() => send("roll")} data-testid="ludo-roll-btn"
            className="pulse-ring ml-auto h-14 flex-1 rounded-full bg-amber-500 font-display text-lg font-bold text-[#0B0D12] active:scale-95 disabled:opacity-60">{t("ludo.roll")}</button>}
          {mineTurn && st.phase === "move" && <p className="ml-auto text-sm font-semibold text-amber-300" data-testid="ludo-pick-hint">{t("ludo.pick")}</p>}
          {!mineTurn && <p className="ml-auto text-sm text-slate-400" data-testid="ludo-wait-hint">{g.pseudos[st.players[st.turn]?.id]} {t("ludo.playing")}</p>}
        </div>
      )}
      <p className="break-all text-[11px] text-slate-600" data-testid="ludo-seed-hash">{t("ludo.seedHash")} {g.seed_hash}</p>
    </div>
  );
}
